import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { requestInitialEmailVerification, EmailVerificationRequestResponse } from '@lendica/auth';

import { useAlert, EmailVerificationDialog } from '@lendica/components';
import { useCompanyContext, useCompanyUserStatus } from './company';

type EmailVerificationDialogState = {
    open: boolean;
    isSubmitting: boolean;
    hasRequested: boolean;
    remainingAttempts?: number;
    rateLimitType?: string;
    cooldownExpiresAt?: number;
};

const initialDialogState: EmailVerificationDialogState = {
    open: false,
    isSubmitting: false,
    hasRequested: false,
};

type EmailVerificationGateState = {
    open: boolean;
    remainingAttempts?: number;
    rateLimitType?: string;
    isCooldown: boolean;
    cooldownMs: number;
    isSubmitting: boolean;
    canRefresh: boolean;
    hasRequested: boolean;
};

type EmailVerificationGateContextValue = {
    state: EmailVerificationGateState;
    resendVerification: () => Promise<void>;
    requireVerification: (payload?: Partial<EmailVerificationRequestResponse>) => void;
    closeDialog: () => void;
    refreshStatus: () => Promise<void>;
};

const EmailVerificationGateContext = createContext<EmailVerificationGateContextValue | null>(null);

const deriveDialogState = (
    prev: EmailVerificationDialogState,
    payload?: Partial<EmailVerificationRequestResponse>,
    options: {
        fallbackWaitSeconds?: number;
        hasRequestedOverride?: boolean;
    } = {}
): EmailVerificationDialogState => {
    const hasRequested =
        typeof options.hasRequestedOverride === 'boolean'
            ? options.hasRequestedOverride
            : prev.hasRequested;

    let cooldownExpiresAt = prev.cooldownExpiresAt;
    if (typeof payload?.remaining_seconds === 'number') {
        cooldownExpiresAt = Date.now() + payload.remaining_seconds * 1000;
    } else if (typeof options.fallbackWaitSeconds === 'number') {
        cooldownExpiresAt = Date.now() + options.fallbackWaitSeconds * 1000;
    }

    return {
        open: true,
        isSubmitting: false,
        hasRequested,
        remainingAttempts: payload?.remaining_attempts_today ?? prev.remainingAttempts,
        rateLimitType: payload?.rate_limit_type ?? prev.rateLimitType,
        cooldownExpiresAt,
    };
};

export const EmailVerificationGateProvider = ({ children }: { children: React.ReactNode }) => {
    const { activeCompanyId, prefetchCompanyUserStatus } = useCompanyContext();
    const { status: userStatus, error: statusError } = useCompanyUserStatus(activeCompanyId);
    const { setErrorMessage, setSuccessMessage } = useAlert();

    const [dialogState, setDialogState] =
        useState<EmailVerificationDialogState>(initialDialogState);
    const [cooldownTick, setCooldownTick] = useState<number>(Date.now());
    const requestedAtRef = useRef<Map<string, number>>(new Map());
    const prevCompanyIdRef = useRef<string | null>(null);

    const emailVerified = userStatus?.email_verified;

    useEffect(() => {
        const prevCompanyId = prevCompanyIdRef.current;
        if (prevCompanyId && prevCompanyId !== activeCompanyId) {
            setDialogState(initialDialogState);
        }
        prevCompanyIdRef.current = activeCompanyId ?? null;
    }, [activeCompanyId]);

    useEffect(() => {
        if (emailVerified) {
            setDialogState(initialDialogState);
        }
    }, [emailVerified]);

    useEffect(() => {
        if (!dialogState.cooldownExpiresAt) return;
        const id = window.setInterval(() => {
            setCooldownTick(Date.now());
        }, 1000);
        return () => {
            window.clearInterval(id);
        };
    }, [dialogState.cooldownExpiresAt]);

    const cooldownMs = useMemo(() => {
        if (!dialogState.cooldownExpiresAt) return 0;
        return Math.max(0, dialogState.cooldownExpiresAt - Date.now());
    }, [dialogState.cooldownExpiresAt, cooldownTick]);

    const isCooldown = cooldownMs > 0;

    const closeDialog = useCallback(() => {
        setDialogState(prev => ({ ...prev, open: false }));
    }, []);

    const refreshStatus = useCallback(async () => {
        if (!activeCompanyId) return;
        await prefetchCompanyUserStatus([activeCompanyId], { force: true });
    }, [activeCompanyId, prefetchCompanyUserStatus]);

    const performRequest = useCallback(
        async ({ force = false, notify = false }: { force?: boolean; notify?: boolean } = {}) => {
            const companyId = activeCompanyId;
            if (!companyId) return;
            if (statusError) return;
            if (emailVerified) {
                setDialogState(initialDialogState);
                return;
            }

            const now = Date.now();
            const lastRequestedAt = requestedAtRef.current.get(companyId);
            const hasRecentRequest = lastRequestedAt && now - lastRequestedAt < 3000;
            const shouldPerform = force || !lastRequestedAt || hasRecentRequest === false;

            setDialogState(prev => ({
                ...prev,
                open: true,
                isSubmitting: shouldPerform,
                hasRequested: prev.hasRequested || shouldPerform,
            }));

            if (!shouldPerform) {
                setDialogState(prev => ({
                    ...prev,
                    isSubmitting: false,
                    open: true,
                    hasRequested: prev.hasRequested || Boolean(lastRequestedAt),
                }));
                return;
            }

            try {
                const response = await requestInitialEmailVerification();
                requestedAtRef.current.set(companyId, now);
                const fallbackWaitSeconds =
                    typeof response?.remaining_seconds === 'number' ? undefined : 45;
                setDialogState(prev =>
                    deriveDialogState(prev, response, {
                        fallbackWaitSeconds,
                        hasRequestedOverride: true,
                    })
                );
                if (notify) {
                    setSuccessMessage(response?.message ?? 'Verification email sent');
                }
            } catch (error: any) {
                requestedAtRef.current.set(companyId, now);
                setDialogState(prev => ({ ...prev, isSubmitting: false }));
                const message =
                    error?.response?.data?.message ??
                    error?.message ??
                    'Unable to send verification email. Please try again later.';
                setErrorMessage(message);
            }
        },
        [activeCompanyId, emailVerified, setErrorMessage, setSuccessMessage, statusError]
    );

    const resendVerification = useCallback(async () => {
        const companyId = activeCompanyId;
        if (!companyId) return;
        if (statusError) return;
        await performRequest({ force: true, notify: true });
    }, [performRequest, activeCompanyId, statusError]);

    const requireVerification = useCallback(
        (payload?: Partial<EmailVerificationRequestResponse>) => {
            setDialogState(prev => deriveDialogState(prev, payload));
        },
        []
    );

    const state: EmailVerificationGateState = useMemo(
        () => ({
            open: dialogState.open,
            remainingAttempts: dialogState.remainingAttempts,
            rateLimitType: dialogState.rateLimitType,
            isCooldown,
            cooldownMs,
            isSubmitting: dialogState.isSubmitting,
            canRefresh: !dialogState.isSubmitting,
            hasRequested: dialogState.hasRequested,
        }),
        [dialogState, isCooldown, cooldownMs]
    );

    const contextValue = useMemo(
        () => ({
            state,
            resendVerification,
            requireVerification,
            closeDialog,
            refreshStatus,
        }),
        [state, resendVerification, requireVerification, closeDialog, refreshStatus]
    );

    return (
        <EmailVerificationGateContext.Provider value={contextValue}>
            {children}
            <EmailVerificationDialog
                open={state.open}
                onClose={closeDialog}
                onResend={resendVerification}
                onRefresh={refreshStatus}
                isCooldown={state.isCooldown}
                cooldownMs={state.cooldownMs}
                isSubmitting={state.isSubmitting}
                rateLimitType={state.rateLimitType}
                hasRequested={state.hasRequested}
            />
        </EmailVerificationGateContext.Provider>
    );
};

export const useEmailVerificationGate = () => {
    const ctx = useContext(EmailVerificationGateContext);
    if (!ctx)
        throw new Error(
            'useEmailVerificationGate must be used within EmailVerificationGateProvider'
        );
    return ctx;
};
